/**
 * storage.js — Subida de archivos al backend de News.
 *
 * Según INSTRUCTIVO_STORAGE_APPS.md: el frontend NUNCA sube directamente
 * al bucket. Todo va como multipart/form-data al backend, que es el único
 * que escribe en Google Cloud Storage con la Service Account.
 *
 * Las funciones mantienen la misma firma que antes para no romper
 * los componentes que las usan (TiptapEditor, ImageUpload).
 */

import { storageService } from './api';

// ─── Resize opcional antes de enviar al backend ───────────────
// Reduce peso sin perder calidad visible — el backend igual valida el archivo.
export const resizeImage = (file, maxWidth = 1600, maxHeight = 1200, quality = 0.85) => {
  return new Promise((resolve, reject) => {
    if (file.type === 'image/gif') { resolve(file); return; }
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxWidth) { height = (height * maxWidth) / width; width = maxWidth; }
        if (height > maxHeight) { width = (width * maxHeight) / height; height = maxHeight; }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        canvas.toBlob(blob => blob ? resolve(new File([blob], file.name, { type: file.type })) : reject(new Error('Error procesando imagen')), file.type, quality);
      };
      img.onerror = () => reject(new Error('Error cargando imagen'));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error('Error leyendo archivo'));
    reader.readAsDataURL(file);
  });
};

/**
 * Sube una imagen al backend de News.
 *
 * @param {File}     file       - Archivo a subir
 * @param {string}   folder     - Carpeta/contexto: 'articles', 'authors', 'editions'
 * @param {Function} onProgress - Callback de progreso (0-100). Aproximado: 50% al enviar, 100% al recibir.
 * @param {string|number} entityId - ID del artículo/autor/edición (requerido para endpoints específicos)
 * @returns {Promise<string>} URL pública del archivo subido
 */
export const uploadImage = async (file, folder = 'articles', onProgress = null, entityId = null) => {
  // Validaciones en frontend para feedback rápido (la real está en el backend)
  if (!file) throw new Error('No se proporcionó archivo');
  const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  if (!allowed.includes(file.type)) throw new Error('Tipo no permitido. Use JPG, PNG, GIF o WebP');
  if (file.size > 5 * 1024 * 1024) throw new Error('Máximo 5 MB');

  // Resize antes de enviar
  let fileToUpload = file;
  try {
    const resized = await resizeImage(file);
    if (resized.size < file.size) fileToUpload = resized;
  } catch { /* si falla el resize, sube el original */ }

  if (onProgress) onProgress(30);

  let result;
  try {
    if (folder === 'articles' && entityId) {
      result = await storageService.uploadArticleHero(entityId, fileToUpload);
    } else if (folder === 'article-images' && entityId) {
      result = await storageService.uploadArticleImage(entityId, fileToUpload);
    } else if (folder === 'authors' && entityId) {
      result = await storageService.uploadAuthorAvatar(entityId, fileToUpload);
    } else {
      // Sin entityId — usar endpoint genérico
      result = await storageService.uploadImagen(fileToUpload, folder);
    }
  } catch (err) {
    throw new Error(err.message || 'Error al subir el archivo');
  }

  if (onProgress) onProgress(100);

  // El backend retorna { gsUri, url } — usamos url (pública https://)
  return result.url || result.signedUrl || result.gsUri;
};

/**
 * Elimina una imagen — delega al backend.
 * Por ahora es un no-op en el frontend: el backend maneja la limpieza
 * al reemplazar archivos. Se mantiene la firma para compatibilidad.
 */
export const deleteImage = async (url) => {
  // El backend borra el archivo anterior al subir uno nuevo.
  // No exponemos endpoint de delete directo desde el frontend por seguridad.
  console.info('[storage] deleteImage no-op — el backend gestiona la limpieza:', url);
};

export default { uploadImage, deleteImage, resizeImage };
